import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { CheckIcon } from '@heroicons/react/24/outline';
import { selectThemes } from 'core/selectors/product/theme';
import { EntityTypes } from 'constants/entities';
import { useGet } from 'core/hooks/useGet';
import { ThemeConfig } from 'types/domain/theme';

/** `config` arrives as JSON on the wire and as an object once edited in place. */
export const parseThemeConfig = (raw: unknown): ThemeConfig => {
  if (!raw) return {};

  if (typeof raw !== 'string') return raw as ThemeConfig;

  try {
    return JSON.parse(raw) as ThemeConfig;
  } catch {
    return {};
  }
};
/**
 * Two chips per row, both drawn from the theme's own config: the accent on its own,
 * then a miniature of a card in that theme.
 */
export function ThemeSwatches({ config }: { config?: ThemeConfig }) {
  const light = config?.modes?.light;
  const accent = config?.modes?.dark?.primary_accent ?? light?.primary_accent;

  return (
    <span className="flex flex-none items-center gap-2">
      <span
        className="h-10 w-10 rounded-md ring-1 ring-inset ring-black/10"
        style={
          accent
            ? { background: accent }
            : // No theme — a hatched blank rather than a colour that means something.
              {
                backgroundImage: 'repeating-linear-gradient(45deg, #e4e4e7 0 4px, #f4f4f5 4px 8px)',
              }
        }
      />
      <span
        className="flex h-10 w-10 flex-col justify-center gap-1 rounded-md px-1.5 ring-1 ring-inset ring-black/10"
        style={{ background: light?.card_bg_color ?? '#f4f4f5' }}
      >
        {accent ? (
          <>
            <span className="h-2.5 w-2.5 rounded-[2px]" style={{ background: accent }} />
            <span
              className="h-1 w-full rounded-full"
              style={{ background: light?.font_color ?? '#a1a1aa', opacity: 0.45 }}
            />
          </>
        ) : (
          <span className="h-1 w-full rounded-full bg-adm-ink3/50" />
        )}
      </span>
    </span>
  );
}

interface ThemePickerProps {
  /** The selected theme; null is the "none" row. */
  value: number | null;
  onChange: (themeId: number | null) => void;
  /** The first row, for "no theme picked". */
  noneLabel?: string;
  noneSubtitle?: string;
  className?: string;
}

/**
 * The theme list: one row per theme with its colours as swatches and its name in its own heading
 * font. Used by the module/course theme setting (ThemeSelect) and the certificate and email designers.
 */
export const ThemePicker: React.FC<ThemePickerProps> = ({
  value,
  onChange,
  noneLabel = 'No theme',
  noneSubtitle = 'Course colours',
  className,
}) => {
  const themes = useSelector(selectThemes);

  useGet(EntityTypes.THEME);

  const options = useMemo(
    () => [
      { id: null as number | null, name: noneLabel, subtitle: noneSubtitle, config: undefined },
      // Skip id-less rows (an empty payload merged into the store), which would read "Theme undefined".
      ...themes
        .filter(theme => theme?.id != null)
        .map(theme => ({
          id: theme.id,
          name: (theme as any).name || `Theme ${theme.id}`,
          subtitle: undefined,
          config: parseThemeConfig((theme as any).config),
        })),
    ],
    [themes, noneLabel, noneSubtitle],
  );

  // Every row renders its name in its own heading font, so they all have to be loaded
  // at once. applyTheme owns `theme-google-fonts` and rewrites it whenever the applied
  // theme changes, so the picker keeps its own link rather than fighting over that one.
  const headingFonts = useMemo(
    () => Array.from(new Set(options.map(o => o.config?.fonts?.heading).filter(Boolean))),
    [options],
  );

  useEffect(() => {
    if (!headingFonts.length) return;

    const id = 'theme-picker-fonts';
    const href = `https://fonts.googleapis.com/css2?${headingFonts
      .map(font => `family=${font.split(',')[0].trim().replace(/ /g, '+')}:wght@400;600;700`)
      .join('&')}&display=swap`;

    const existing = document.getElementById(id) as HTMLLinkElement | null;

    if (existing) {
      existing.href = href;
      return;
    }

    document.head.appendChild(Object.assign(document.createElement('link'), { id, rel: 'stylesheet', href }));
  }, [headingFonts]);

  return (
    <div className={classNames('flex flex-col gap-2 font-adm', className)}>
      {options.map(option => {
        const selected = value === option.id;

        return (
          <button
            type="button"
            key={option.id ?? 'none'}
            aria-pressed={selected}
            onClick={() => onChange(option.id)}
            className={classNames(
              'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-adm-accent',
              selected ? 'border-adm-accent bg-adm-accentWeak' : 'border-adm-line bg-adm-surface hover:border-adm-ink3',
            )}
          >
            <ThemeSwatches config={option.config} />
            <span className="min-w-0 flex-1">
              <span
                className="block truncate text-[15px] font-semibold text-adm-ink"
                style={option.config?.fonts?.heading ? { fontFamily: option.config.fonts.heading } : undefined}
              >
                {option.name}
              </span>
              {option.subtitle && <span className="block text-[13px] text-adm-ink2">{option.subtitle}</span>}
            </span>
            {selected && <CheckIcon className="h-5 w-5 flex-none text-adm-accent" />}
          </button>
        );
      })}
    </div>
  );
};

export default ThemePicker;
